"use client";

import Image from "next/image";
import Link from "next/link";
import { useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import { Mail, Plus, Search, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { FAQ_CATEGORIES, FAQ_QUESTIONS, type FaqCategory } from "@/lib/faq";

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.2, 0, 0, 1] },
    },
  };
}

export function FaqPage() {
  const reduce = useReducedMotion() ?? false;
  const [category, setCategory] = useState<FaqCategory>("All");
  const [query, setQuery] = useState("");
  const [openQuestion, setOpenQuestion] = useState<string | null>(
    FAQ_QUESTIONS[0].question
  );

  const filteredQuestions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return FAQ_QUESTIONS.filter((item) => {
      const matchesCategory = category === "All" || item.category === category;
      const matchesQuery =
        !normalizedQuery ||
        `${item.question} ${item.answer}`.toLowerCase().includes(normalizedQuery);

      return matchesCategory && matchesQuery;
    });
  }, [category, query]);

  function clearFilters() {
    setQuery("");
    setCategory("All");
  }

  return (
    <div className="bg-neutral-0">
      <section
        className="relative overflow-hidden bg-neutral-0"
        aria-labelledby="faq-page-heading"
      >
        <Image
          src="/hero-bg.png"
          alt=""
          width={1975}
          height={796}
          priority
          className="pointer-events-none absolute inset-x-0 top-0 h-full w-full select-none object-cover object-top opacity-40"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_-10%,rgba(14,58,138,0.10),transparent_60%)]"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-[120px] bg-gradient-to-b from-transparent to-neutral-0"
          aria-hidden="true"
        />

        <motion.div
          variants={container}
          initial="hidden"
          animate="visible"
          className="relative z-10 mx-auto flex max-w-[840px] flex-col items-center px-6 pb-16 pt-16 text-center sm:px-10 sm:pb-20 sm:pt-20 lg:pb-24 lg:pt-28"
        >
          <motion.p
            variants={fadeUp(reduce)}
            className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary-100 bg-white/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-primary-500 backdrop-blur-sm"
          >
            <Sparkles
              className="star-float h-3.5 w-3.5 text-accent-500"
              strokeWidth={2}
              aria-hidden="true"
            />
            Help Centre
          </motion.p>

          <motion.h1
            id="faq-page-heading"
            variants={fadeUp(reduce)}
            className="text-[clamp(2.15rem,4.6vw,3.5rem)] font-semibold leading-[1.08] tracking-[-0.015em] text-primary-800"
          >
            Questions about gold?
            <span className="block">
              We have the <span className="font-instrument italic font-normal">answers.</span>
            </span>
          </motion.h1>

          <motion.p
            variants={fadeUp(reduce)}
            className="mt-6 max-w-[620px] text-lg leading-relaxed text-text-body"
          >
            Learn how buying, selling, storing, and redeeming real 24K gold
            works with JustGold.
          </motion.p>
        </motion.div>
      </section>

      <section
        className="bg-neutral-0 pb-16 sm:pb-20 lg:pb-24"
        aria-labelledby="faq-list-heading"
      >
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8">
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="mx-auto max-w-[900px]"
          >
            <motion.div variants={fadeUp(reduce)}>
              <label htmlFor="faq-search" className="sr-only">
                Search frequently asked questions
              </label>
              <div className="flex min-h-14 items-center gap-3 rounded-card border border-[#e3ecfb] bg-white px-5 shadow-[0_8px_30px_-20px_rgba(10,45,110,0.22)] transition-colors focus-within:border-primary-300 focus-within:ring-[3px] focus-within:ring-primary-600/10">
                <Search
                  className="h-5 w-5 shrink-0 text-primary-500"
                  strokeWidth={1.8}
                  aria-hidden="true"
                />
                <input
                  id="faq-search"
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search questions"
                  className="min-w-0 flex-1 bg-transparent py-4 text-base text-primary-900 outline-none placeholder:text-neutral-400"
                />
                <span className="hidden text-xs text-text-muted sm:block">
                  {filteredQuestions.length} answers
                </span>
              </div>
            </motion.div>

            <motion.div
              variants={fadeUp(reduce)}
              className="mt-5 flex gap-2.5 overflow-x-auto pb-2 sm:flex-wrap"
              role="group"
              aria-label="Filter questions by topic"
            >
              {FAQ_CATEGORIES.map((item) => {
                const active = category === item;

                return (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setCategory(item)}
                    aria-pressed={active}
                    className={cn(
                      "min-h-11 shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40",
                      active
                        ? "border-primary-600 bg-primary-600 text-white"
                        : "border-[#e3ecfb] bg-white text-primary-700 hover:border-primary-200 hover:bg-primary-50/70"
                    )}
                  >
                    {item}
                  </button>
                );
              })}
            </motion.div>

            <motion.div variants={fadeUp(reduce)} className="mb-6 mt-10">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-500">
                {category === "All" ? "Popular Questions" : category}
              </p>
              <h2
                id="faq-list-heading"
                className="text-[clamp(1.75rem,3.5vw,2.5rem)] font-semibold leading-[1.15] tracking-[-0.015em] text-primary-900"
              >
                Frequently asked questions
              </h2>
            </motion.div>

            {filteredQuestions.length > 0 ? (
              <motion.div
                key={`${category}-${query}`}
                initial="hidden"
                animate="visible"
                variants={container}
                className="flex flex-col gap-3"
              >
                {filteredQuestions.map((item) => {
                  const isOpen = openQuestion === item.question;
                  const panelId = `faq-panel-${FAQ_QUESTIONS.indexOf(item)}`;

                  return (
                    <motion.article
                      key={item.question}
                      variants={fadeUp(reduce)}
                      className={cn(
                        "overflow-hidden rounded-card border bg-white transition-colors",
                        isOpen
                          ? "border-primary-200 shadow-[0_10px_30px_-18px_rgba(10,45,110,0.18)]"
                          : "border-[#e3ecfb]"
                      )}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setOpenQuestion(isOpen ? null : item.question)
                        }
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        className="flex min-h-16 w-full items-start justify-between gap-6 px-5 py-5 text-left sm:px-6 sm:py-6"
                      >
                        <span className="text-base font-semibold leading-snug text-primary-900 sm:text-lg">
                          {item.question}
                        </span>
                        <span
                          className={cn(
                            "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors duration-200",
                            isOpen
                              ? "border-primary-600 bg-primary-600 text-white"
                              : "border-[#e3ecfb] bg-[#f7faff] text-primary-700"
                          )}
                          aria-hidden="true"
                        >
                          <Plus
                            className={cn(
                              "h-4 w-4 transition-transform duration-300",
                              isOpen ? "rotate-45" : "rotate-0"
                            )}
                            strokeWidth={2.2}
                          />
                        </span>
                      </button>

                      <AnimatePresence initial={false}>
                        {isOpen ? (
                          <motion.div
                            id={panelId}
                            role="region"
                            initial={
                              reduce
                                ? { opacity: 1 }
                                : { opacity: 0, height: 0 }
                            }
                            animate={{ opacity: 1, height: "auto" }}
                            exit={
                              reduce
                                ? { opacity: 0 }
                                : { opacity: 0, height: 0 }
                            }
                            transition={{
                              duration: reduce ? 0.01 : 0.28,
                              ease: [0.2, 0, 0, 1],
                            }}
                            className="overflow-hidden"
                          >
                            <p className="max-w-[720px] px-5 pb-6 pr-14 text-base leading-[1.6] text-text-body sm:px-6 sm:pr-16">
                              {item.answer}
                            </p>
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                    </motion.article>
                  );
                })}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="rounded-card border border-[#e3ecfb] bg-primary-50/40 px-6 py-12 text-center"
              >
                <p className="text-lg font-semibold text-primary-900">
                  No answers found
                </p>
                <p className="mt-2 text-sm leading-relaxed text-text-body">
                  Try another search or view all questions.
                </p>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="mt-5 text-sm font-semibold text-primary-600 underline decoration-primary-200 underline-offset-4 transition-colors hover:text-primary-800"
                >
                  Clear search
                </button>
              </motion.div>
            )}

            <motion.div
              variants={fadeUp(reduce)}
              className="mt-12 flex flex-col items-start justify-between gap-5 border-t border-[#e3ecfb] pt-8 sm:flex-row sm:items-center"
            >
              <div>
                <h3 className="text-lg font-semibold text-primary-900">
                  Still have a question?
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-text-body">
                  Our support team is ready to help.
                </p>
              </div>
              <Link
                href="/contact-us"
                className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-primary-200 bg-white px-5 py-3 text-sm font-semibold text-primary-700 transition-colors duration-200 hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
              >
                <Mail className="h-4 w-4" strokeWidth={1.8} aria-hidden="true" />
                Contact support
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
